import { StyledRechartsResponsiveContainer } from 'pages/Dashboard/components/styled';
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';

/**
 * Fixed Y-axis layer - positioned absolutely, only shows Y-axis
 */
const FixedYAxisContainer = styled(StyledRechartsResponsiveContainer)`
    position: absolute;
    top: 0;
    left: 0;
    pointer-events: none;
    z-index: 2;

    text {
        fill: ${tokens.text.primary};
    }

    .recharts-bar {
        display: none;
    }
    .recharts-xAxis,
    .recharts-xAxis-tick-labels,
    .recharts-label-list {
        display: none;
    }
    .recharts-legend-wrapper {
        display: none;
    }
    .recharts-cartesian-grid {
        display: none;
    }
`;

/**
 * Scrollable data layer - hides Y-axis, shows X-axis and bars
 */
const ScrollableDataContainer = styled(StyledRechartsResponsiveContainer)`
    .recharts-yAxis {
        display: none;
    }

    .recharts-legend-wrapper {
        display: none;
    }

    min-width: 100%;
`;

/**
 * Fade overlay to indicate scrollable content
 */
const FadeOverlay = styled.div<{
    $direction: 'left' | 'right';
    $visible: boolean;
    $offset?: string;
}>`
    position: absolute;
    top: 0;
    ${({ $direction, $offset }) =>
        $direction === 'left'
            ? $offset
                ? `left: ${$offset};`
                : 'left: 0px;'
            : 'right: 0px;'}
    width: 40px;
    height: 100%;
    pointer-events: none;
    z-index: 3;
    opacity: ${({ $visible }) => ($visible ? 1 : 0)};
    transition: opacity 0.2s ease-in-out;
    background: linear-gradient(
        to ${({ $direction }) => ($direction === 'left' ? 'right' : 'left')},
        ${tokens.background.default},
        transparent
    );
`;

const RootContainer = styled.div<{ $height: string | number }>`
    display: flex;
    flex-direction: column;
    height: ${({ $height }) => (typeof $height === 'number' ? `${$height}px` : $height)};
    width: 100%;
`;

const NonLegendWrapper = styled.div`
    position: relative;
    flex: 1;
    width: 100%;
`;

const ScrollableBarsContainer = styled.div<{ $yAxisWidth: number }>`
    height: 100%;
    width: calc(100% - ${({ $yAxisWidth }) => $yAxisWidth}px);
    float: right;
    overflow-x: scroll;
    overflow-y: hidden;
`;

const LegendContainer = styled.div`
    padding-top: 1em;
    text-align: center;
`;

type UseScrollUtilsProps = {
    scrollContainerRef: React.RefObject<HTMLDivElement>;
    dataLength: number;
    totalWidthPercent: number;
    startScrolledToEnd: boolean;
};

/**
 * Custom hook to manage scroll stuff, including:
 * - "start scrolled to end" for bar chart content
 * - "fade" overlays at whichever end(s) of the chart have overflow beyond them
 */
function useScrollUtils({
    scrollContainerRef,
    dataLength,
    totalWidthPercent,
    startScrolledToEnd,
}: UseScrollUtilsProps) {
    const [showLeftFade, setShowLeftFade] = useState(false);
    const [showRightFade, setShowRightFade] = useState(false);

    // Check scroll position to show/hide fade overlays
    const updateScrollFades = useCallback(() => {
        const container = scrollContainerRef.current;
        if (!container) {
            setShowLeftFade(false);
            setShowRightFade(false);
            return;
        }

        const scrollLeft = container.scrollLeft;
        const scrollWidth = container.scrollWidth;
        const clientWidth = container.clientWidth;

        // if scrolled right from start (with 1px threshold)
        setShowLeftFade(scrollLeft > 1);

        // if not scrolled to end (with 1px threshold)
        setShowRightFade(scrollLeft < scrollWidth - clientWidth - 1);
    }, [scrollContainerRef]);

    useEffect(() => {
        // Scroll to the end horizontally on mount if necessary
        if (startScrolledToEnd && scrollContainerRef.current) {
            scrollContainerRef.current.scrollLeft =
                scrollContainerRef.current.scrollWidth;
        }
        updateScrollFades();
    }, [scrollContainerRef, startScrolledToEnd, updateScrollFades]);

    // Update fades when data length changes (affects scrollability)
    useEffect(() => {
        updateScrollFades();
    }, [dataLength, totalWidthPercent, updateScrollFades]);

    // Handle horizontal scroll with regular mouse wheel
    const handleWheel = useCallback(
        (e: WheelEvent) => {
            if (!scrollContainerRef.current) {
                return;
            }

            // Only intercept if scrolling vertically (normal mouse wheel)
            // and the container is horizontally scrollable
            // Don't intercept if shift is held (user wants native smooth scroll)
            if (!e.shiftKey && Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
                e.preventDefault();
                scrollContainerRef.current.scrollBy({
                    left: e.deltaY,
                    behavior: 'smooth',
                });
                updateScrollFades();
            }
        },
        [scrollContainerRef, updateScrollFades]
    );

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) {
            return;
        }

        container.addEventListener('wheel', handleWheel, { passive: false });
        return () => {
            container.removeEventListener('wheel', handleWheel);
        };
    }, [handleWheel, scrollContainerRef]);

    return {
        showLeftFade,
        showRightFade,
        updateScrollFades,
    };
}

type ScrollableBarChartProps = {
    /**
     * Render function that returns the BarChart configuration
     * Should include all XAxis, YAxis, Bar, and other Recharts components
     * The function receives a 'layer' param: 'fixed-yaxis' or 'scrollable-data'
     *
     * IMPORTANT: Both layers should have identical margin configurations
     * with left margin set to 0 or minimal (e.g., { left: 0, right: 12, top: 20, bottom: 5 })
     */
    children: (p: {
        layer: 'fixed-yaxis' | 'scrollable-data';
        yAxisWidth: number | 'auto' | undefined;
    }) => ReactElement;
    /**
     * Optional legend element to render below the scrollable chart
     * This will be fixed and not scroll with the chart content
     */
    legend?: ReactElement;
    /**
     * Width of the Y-axis area in pixels (default: 80)
     * The scrollable container will be offset by this amount
     */
    yAxisWidth?: number;
    /**
     * Number of data items (bars) to display
     * Used with minBarWidthPercent to calculate total scroll width
     */
    dataLength: number;
    /**
     * Minimum width per bar as a percentage of the scrollable container (default: 10)
     * E.g., 10 means each bar needs 10% of the visible area width
     */
    minBarWidthPercent?: number;
    /**
     * Height of the chart container (default: "100%")
     */
    height?: string | number;

    /**
     * Whether the bars should start scrolled all the way to the end of the
     * container.
     */
    startScrolledToEnd?: boolean;
};

/**
 * ScrollableBarChart - A wrapper component for horizontally scrollable bar charts
 *
 * Renders 2 versions of the recharts chart, called 'layer's below.
 *  - layer 1: fixed (y-axis)
 *  - layer 2: scrollable (x-axis and bars)
 *
 * @example
 * ```tsx
 * <ScrollableBarChart
 *     yAxisWidth={80}
 *     dataLength={chartData.length}
 *     minBarWidthPercent={10}
 * >
 *     {(layer) => (
 *         <BarChart data={chartData} margin={{ top: 20, right: 15, left: 15, bottom: 5 }}>
 *             <CartesianGrid strokeDasharray="3 3" />
 *             <XAxis dataKey="date" />
 *             <YAxis width={80} />
 *             <Bar dataKey="value" fill={theme.dashboardAccentPrimary} />
 *         </BarChart>
 *     )}
 * </ScrollableBarChart>
 * ```
 */
export function ScrollableBarChart({
    children,
    legend,
    yAxisWidth = 80,
    dataLength,
    minBarWidthPercent = 10,
    height = '100%',
    startScrolledToEnd = false,
}: ScrollableBarChartProps) {
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // Calculate total width as percentage
    // E.g., 20 bars * 10% = 200% (will scroll)
    // E.g., 8 bars * 10% = 80% (will not scroll, min 100%)
    const totalWidthPercent = Math.max(100, dataLength * minBarWidthPercent);

    // Use the scroll fades hook to manage fade overlay visibility and wheel handling
    const { showLeftFade, showRightFade, updateScrollFades } = useScrollUtils({
        scrollContainerRef,
        dataLength,
        totalWidthPercent,
        startScrolledToEnd,
    });

    return (
        <RootContainer $height={height}>
            <NonLegendWrapper>
                {/* Fixed Y-axis layer - absolutely positioned, full width */}
                <FixedYAxisContainer width="100%" height="100%">
                    {children({ layer: 'fixed-yaxis', yAxisWidth })}
                </FixedYAxisContainer>

                {/* Fade overlays to indicate scrollable content */}
                <FadeOverlay
                    $direction="left"
                    $visible={showLeftFade}
                    $offset={`${yAxisWidth}px`}
                />
                <FadeOverlay $direction="right" $visible={showRightFade} />

                {/* Scrollable data + X-axis layer - offset from left by Y-axis width */}
                <ScrollableBarsContainer
                    $yAxisWidth={yAxisWidth}
                    ref={scrollContainerRef}
                    onScroll={updateScrollFades}
                >
                    <ScrollableDataContainer
                        width={`${totalWidthPercent}%`}
                        height="100%"
                    >
                        {children({ layer: 'scrollable-data', yAxisWidth: 0 })}
                    </ScrollableDataContainer>
                </ScrollableBarsContainer>
            </NonLegendWrapper>

            {/* Fixed legend below the chart */}
            {legend && <LegendContainer>{legend}</LegendContainer>}
        </RootContainer>
    );
}
